/**
 * AmbassadorDashboard - Ambassador Self-Service Dashboard
 *
 * Shows referral code, stats, linked properties, and commission history.
 * This is the standalone version of the self-service view from POSAmbassadors.jsx.
 *
 * URL: /ambassador
 */

import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useConfig } from '../../contexts/ConfigContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorDisplay from '../../components/ErrorDisplay';
import DataTable, { StatusBadge } from '../../components/DataTable';
import EmptyState from '../../components/EmptyState';
import { formatDate } from '../../utils/formatters';
import AmbassadorSaveTip from './AmbassadorSaveTip';
import { POS_GOLD } from '../../config/posRoomStatus';


/** Referral code from pos_referral_codes table */
interface ReferralCode {
  code: string;
  uses_count: number;
  is_active: boolean;
}

/** Unified ambassador_properties with pipeline status */
interface AmbassadorProperty {
  id: string;
  property_name: string;
  city: string | null;
  pipeline_status: string;
  commission_rate: number;
  last_activity_at: string;
  hosp_property_id: string | null;
}

/** Commission record from ambassador_commissions + hosp_properties join */
interface AmbassadorCommission {
  id: string;
  commission_amount: string;
  commission_rate: number;
  base_amount: string;
  status: string;
  created_at: string | null;
  property_id: string | null;
  hosp_properties: { property_name: string } | null;
}

/** Proposal submitted by ambassador */
interface Proposal {
  id: string;
  reference_code: string;
  property_name: string;
  property_city: string | null;
  selected_tier: string;
  annual_total: number;
  status: string;
  created_at: string;
}

/** Vendor details stored as JSONB on users table */
interface VendorDetails {
  status?: string;
  [key: string]: unknown;
}


const COMMISSION_STATUS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: 'Pending', color: 'var(--color-warning)', bg: 'var(--color-warning-bg)' },
  approved: { label: 'Approved', color: 'var(--color-info)', bg: 'var(--color-info-bg)' },
  paid: { label: 'Paid', color: 'var(--color-success)', bg: 'var(--color-success-bg)' },
  voided: { label: 'Voided', color: 'var(--color-text-light)', bg: 'var(--color-border)' }
};

interface StatCardProps {
  label: string;
  value: string | number;
  highlight?: boolean;
}

function StatCard({ label, value, highlight }: StatCardProps) {
  return (
    <div className="card" style={{ padding: '16px', textAlign: 'center' }}>
      <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-light)', margin: '0 0 4px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
        {label}
      </p>
      <p style={{
        fontSize: 'var(--text-4xl)',
        fontWeight: 700,
        margin: 0,
        color: highlight ? POS_GOLD : 'var(--color-sidebar)'
      }}>
        {value}
      </p>
    </div>
  );
}

function AmbassadorDashboard() {
  const { supabase, user } = useAuth();
  const { formatCurrency } = useConfig();

  const [properties, setProperties] = useState<AmbassadorProperty[]>([]);
  const [commissions, setCommissions] = useState<AmbassadorCommission[]>([]);
  const [proposals, setProposals] = useState<Proposal[]>([]);
  const [referralCode, setReferralCode] = useState<ReferralCode | null>(null);
  const [hasVendorDetails, setHasVendorDetails] = useState<boolean>(false);
  const [vendorStatus, setVendorStatus] = useState<string | null>(null);
  const [agentCode, setAgentCode] = useState<{ code: string; commission_rate: number; times_used: number; is_active: boolean } | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string>('');

  // Key the load on the user's id, not the user object. AuthContext returns a
  // new user object on every token refresh / tab refocus; keying on the object
  // reloaded the page each time.
  const userId = user?.id;
  const loadData = useCallback(async () => {
    if (!userId) return;
    try {
      setLoading(true);
      setError(null);

      // Get referral code
      const { data: codes } = await supabase
        .from('pos_referral_codes')
        .select('code, uses_count, is_active')
        .eq('user_id', userId)
        .eq('is_active', true)
        .limit(1);

      if (codes && codes.length > 0) {
        setReferralCode(codes[0] as ReferralCode);
      }

      // Get linked properties
      const { data: props } = await supabase
        .from('ambassador_properties')
        .select('id, property_name, city, pipeline_status, commission_rate, last_activity_at, hosp_property_id')
        .eq('user_id', userId)
        .order('last_activity_at', { ascending: false });

      setProperties((props || []) as unknown as AmbassadorProperty[]);

      // Get commission records
      const { data: comms } = await supabase
        .from('ambassador_commissions')
        .select('id, commission_amount, commission_rate, base_amount, status, created_at, property_id, hosp_properties(property_name)')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });

      setCommissions((comms || []) as unknown as AmbassadorCommission[]);

      // Get proposals submitted by this ambassador
      const { data: proposalData } = await supabase
        .from('pos_proposals')
        .select('id, reference_code, property_name, property_city, selected_tier, annual_total, status, created_at')
        .eq('ambassador_user_id', userId)
        .order('created_at', { ascending: false });

      setProposals((proposalData || []) as Proposal[]);

      // Check vendor/payout status
      try {
        const { data: userData } = await supabase
          .from('users')
          .select('vendor_details')
          .eq('id', userId)
          .single();

        const vd = userData?.vendor_details as VendorDetails | null;
        if (vd?.status) {
          setVendorStatus(vd.status);
          setHasVendorDetails(vd.status === 'submitted' || vd.status === 'approved');
        }
      } catch {
        // vendor_details column may not exist yet — ignore
      }
      // Check for agent commission code linked to this user
      const { data: agentVoucher } = await supabase
        .from('partner_vouchers')
        .select('code, percentage_value, times_used, is_active')
        .eq('agent_user_id', userId)
        .eq('payout_type', 'commission')
        .is('parent_voucher_id', null)
        .maybeSingle();

      if (agentVoucher) {
        setAgentCode({
          code: agentVoucher.code,
          commission_rate: agentVoucher.percentage_value || 0,
          times_used: agentVoucher.times_used || 0,
          is_active: agentVoucher.is_active,
        });
      }

    } catch (err: unknown) {
      console.error('Error loading ambassador data:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [supabase, userId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const copyToClipboard = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      setTimeout(() => setCopied(''), 2000);
    } catch (err: unknown) {
      console.error('Copy failed:', err);
    }
  };

  const buildAmbassadorPitch = () => {
    const refPart = referralCode ? `\nMention my name or use my referral code *${referralCode.code}* when you apply.\n` : '\n';
    return `Hi! I'm a Peace of Sleep Brand Ambassador and thought your property might be a great fit for the programme.

Peace of Sleep is a hospitality certification that shows guests your property takes sleep hygiene seriously — professional mattress sanitisation on a regular schedule. There are three tiers (Assured, Distinguished, Signature) depending on how often services are done.

Here are some quick links:

Brochure: https://peaceofsleep.vip/za/brochure/
Pricing & Tiers: https://cleanmybed.com/pos/pricing/
Apply to Join: https://cleanmybed.com/pos/join/
${refPart}
Happy to answer any questions!`;
  };

  const handleWhatsAppTemplate = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(buildAmbassadorPitch())}`, '_blank');
  };

  const handleSmsTemplate = () => {
    window.location.href = `sms:?body=${encodeURIComponent(buildAmbassadorPitch())}`;
  };

  if (loading) return <LoadingSpinner />;
  if (error) return <ErrorDisplay error={error} onRetry={loadData} />;

  const totalEarned = commissions.reduce((s, c) => s + (parseFloat(c.commission_amount) || 0), 0);
  const totalPaid = commissions.filter(c => c.status === 'paid').reduce((s, c) => s + (parseFloat(c.commission_amount) || 0), 0);
  const totalPending = totalEarned - totalPaid;
  const referralLink = referralCode ? `https://peaceofsleep.vip/za/join?ref=${referralCode.code}` : '';

  return (
    <div className="page-content">
      {/* Welcome Header */}
      <div className="flex items-center gap-md mb-lg">
        <div>
          <h1 className="page-title mb-xs">Welcome, {user?.first_name || 'Ambassador'}</h1>
          <span style={{
            display: 'inline-block',
            padding: '4px 12px',
            borderRadius: 'var(--radius-lg)',
            fontSize: 'var(--text-md)',
            fontWeight: 600,
            color: 'var(--color-white)',
            background: POS_GOLD
          }}>Brand Ambassador</span>
        </div>
      </div>

      {/* First-visit "Save for next time" nudge — bookmark / home-screen /
          password save. Hides once dismissed, never appears again on this
          device. Self-hides for users already in standalone PWA mode. */}
      <AmbassadorSaveTip />

      {/* Payout Setup Banner */}
      {!hasVendorDetails && vendorStatus !== 'submitted' && (
        <div style={{
          background: `linear-gradient(135deg, ${POS_GOLD}, #d4a843)`,
          borderRadius: 'var(--radius)',
          padding: '16px 24px',
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          <div>
            <p style={{ margin: 0, fontWeight: 600, color: 'var(--color-sidebar)' }}>
              Set up your payout details
            </p>
            <p style={{ margin: '4px 0 0', fontSize: 'var(--text-base)', color: 'rgba(0,0,0,0.7)' }}>
              Submit your banking details to receive commission payments.
            </p>
          </div>
          <Link
            to="/ambassador/payout-setup"
            style={{
              padding: '8px 20px',
              background: 'var(--color-sidebar)',
              color: 'var(--color-white)',
              borderRadius: 'var(--radius-md)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--text-md)',
              letterSpacing: '0.5px'
            }}
          >
            Set Up Now
          </Link>
        </div>
      )}

      {vendorStatus === 'submitted' && (
        <div className="alert alert-info mb-xl">
          Your payout details are under review. You&apos;ll be notified once approved.
        </div>
      )}

      {/* Agent Commission Code */}
      {agentCode && (
        <div style={{
          background: 'linear-gradient(135deg, #2d1b4e, #1a1a2e)',
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          marginBottom: '24px',
          color: 'var(--color-white)'
        }}>
          <h3 style={{ margin: '0 0 4px', color: 'var(--color-white)', fontSize: 'var(--text-base)', fontWeight: 600 }}>Agent Commission Code</h3>
          <p style={{ margin: '0 0 16px', fontSize: 'var(--text-md)', color: 'rgba(255,255,255,0.6)' }}>
            Share this code with residential customers to earn commission
          </p>
          <div className="flex items-center gap-md flex-wrap">
            <div style={{
              background: 'var(--color-primary)',
              color: 'var(--color-white)',
              padding: '10px 24px',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--text-3xl)',
              fontWeight: 700,
              fontFamily: 'monospace',
              letterSpacing: '2px',
              cursor: 'pointer',
            }} onClick={() => { navigator.clipboard.writeText(agentCode.code); setCopied('agent'); setTimeout(() => setCopied(''), 2000); }}>
              {agentCode.code}
            </div>
            <div>
              <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'rgba(255,255,255,0.8)' }}>
                {agentCode.commission_rate}% commission · {agentCode.times_used} uses
              </p>
              <p style={{ margin: '4px 0 0', fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.5)' }}>
                {agentCode.is_active ? 'Active' : 'Inactive'} · Click code to copy
              </p>
            </div>
            {copied === 'agent' && <span style={{ fontSize: 'var(--text-sm)', color: POS_GOLD }}>Copied!</span>}
          </div>
          <div style={{ marginTop: '12px' }}>
            <a
              href={`https://cleanmybed.com/agent/?code=${agentCode.code}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{ fontSize: 'var(--text-sm)', color: 'rgba(255,255,255,0.5)', textDecoration: 'none' }}
            >
              View full tracking dashboard &rarr;
            </a>
          </div>
        </div>
      )}

      {/* Referral Code Card */}
      {referralCode && (
        <div style={{
          background: `linear-gradient(135deg, #1a1a1a, #333)`,
          borderRadius: 'var(--radius-lg)',
          padding: '24px',
          marginBottom: '24px',
          color: 'var(--color-white)'
        }}>
          <h3 style={{ margin: '0 0 16px', color: POS_GOLD }}>Your Referral Code</h3>
          <div className="flex items-center gap-md flex-wrap">
            <div style={{
              background: POS_GOLD,
              color: 'var(--color-sidebar)',
              padding: '12px 24px',
              borderRadius: 'var(--radius)',
              fontSize: 'var(--text-4xl)',
              fontWeight: 700,
              letterSpacing: '2px',
              fontFamily: 'monospace'
            }}>
              {referralCode.code}
            </div>
            <button
              className="btn btn-sm"
              style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--color-white)', border: 'none' }}
              onClick={() => copyToClipboard(referralCode.code, 'code')}
            >
              {copied === 'code' ? 'Copied!' : 'Copy Code'}
            </button>
          </div>
          <div style={{ marginTop: '16px' }}>
            <p style={{ margin: '0 0 8px', color: 'var(--color-text-light)', fontSize: 'var(--text-md)' }}>Share this link with properties:</p>
            <div className="flex items-center gap-sm flex-wrap">
              <code style={{
                background: 'rgba(255,255,255,0.1)',
                padding: '8px 12px',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--text-md)',
                wordBreak: 'break-all'
              }}>
                {referralLink}
              </code>
              <button
                className="btn btn-sm"
                style={{ background: 'rgba(255,255,255,0.15)', color: 'var(--color-white)', border: 'none' }}
                onClick={() => copyToClipboard(referralLink, 'link')}
              >
                {copied === 'link' ? 'Copied!' : 'Copy Link'}
              </button>
            </div>
          </div>
          {/* QR Code */}
          <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
            <img
              src={`https://api.qrserver.com/v1/create-qr-code/?size=100x100&data=${encodeURIComponent(referralLink)}`}
              alt="Referral QR Code"
              style={{ width: '80px', height: '80px', borderRadius: 'var(--radius)', background: 'var(--color-white)', padding: '4px' }}
            />
            <p style={{ margin: 0, color: 'var(--color-text-light)', fontSize: 'var(--text-sm)' }}>
              Used {referralCode.uses_count || 0} time{referralCode.uses_count !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
      )}

      {/* Ambassador Toolkit */}
      <div style={{
        background: 'var(--color-white)',
        borderRadius: 'var(--radius-lg)',
        padding: '24px',
        marginBottom: '24px',
        border: '1px solid #e5e7eb'
      }}>
        <h3 style={{ margin: '0 0 16px', fontSize: 'var(--text-xl)', fontWeight: 600, color: 'var(--color-sidebar)' }}>
          Your Toolkit
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '12px'
        }}>
          {/* WhatsApp Template */}
          <button
            onClick={handleWhatsAppTemplate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-success)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 'var(--text-base)',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>💬</span>
            <span>WhatsApp Template</span>
          </button>

          {/* SMS Template */}
          <button
            onClick={handleSmsTemplate}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-primary)',
              color: 'var(--color-white)',
              border: 'none',
              borderRadius: 'var(--radius)',
              cursor: 'pointer',
              fontWeight: 600,
              fontSize: 'var(--text-base)',
              textAlign: 'left'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>📲</span>
            <span>SMS Template</span>
          </button>

          {/* Brochure */}
          <a
            href="https://peaceofsleep.vip/za/brochure/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-bg)',
              color: 'var(--color-sidebar)',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--text-base)'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>📄</span>
            <span>Digital Brochure</span>
          </a>

          {/* Property Apply Link */}
          <a
            href="https://cleanmybed.com/pos/join/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-bg)',
              color: 'var(--color-sidebar)',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--text-base)'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>📋</span>
            <span>Property Apply Form</span>
          </a>

          {/* Pricing & Tiers */}
          <a
            href="https://cleanmybed.com/pos/pricing/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-bg)',
              color: 'var(--color-sidebar)',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--text-base)'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>💰</span>
            <span>Pricing & Tiers</span>
          </a>

          {/* POS Website */}
          <a
            href="https://peaceofsleep.vip/"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              padding: '14px 16px',
              background: 'var(--color-bg)',
              color: 'var(--color-sidebar)',
              border: '1px solid #e5e7eb',
              borderRadius: 'var(--radius)',
              textDecoration: 'none',
              fontWeight: 600,
              fontSize: 'var(--text-base)'
            }}
          >
            <span style={{ fontSize: 'var(--text-3xl)' }}>🌐</span>
            <span>Peace of Sleep Website</span>
          </a>
        </div>
      </div>

      {/* Property Leads Summary */}
      <Link to="/ambassador/properties" style={{ textDecoration: 'none', display: 'block', marginBottom: '12px' }}>
        <div className="card" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
          <div>
            <h3 style={{ margin: '0 0 4px', fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-sidebar)' }}>My Properties</h3>
            <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
              {properties.length} confirmed
            </p>
          </div>
          <span style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-2xl)' }}>&rsaquo;</span>
        </div>
      </Link>

      {/* Proposals Summary */}
      {proposals.length > 0 && (
        <Link to="/ambassador/proposals" style={{ textDecoration: 'none', display: 'block', marginBottom: '24px' }}>
          <div className="card" style={{ padding: '16px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }}>
            <div>
              <h3 style={{ margin: '0 0 4px', fontSize: 'var(--text-lg)', fontWeight: 600, color: 'var(--color-sidebar)' }}>My Proposals</h3>
              <p style={{ margin: 0, fontSize: 'var(--text-md)', color: 'var(--color-text-light)' }}>
                {proposals.length} proposal{proposals.length !== 1 ? 's' : ''}
                {proposals.filter(p => p.status === 'pending_approval').length > 0 && (
                  <span style={{ color: 'var(--color-warning)' }}> &middot; {proposals.filter(p => p.status === 'pending_approval').length} pending</span>
                )}
                {proposals.filter(p => p.status === 'viewed').length > 0 && (
                  <span style={{ color: 'var(--color-warning)' }}> &middot; {proposals.filter(p => p.status === 'viewed').length} viewed</span>
                )}
                {proposals.filter(p => p.status === 'accepted').length > 0 && (
                  <span style={{ color: 'var(--color-success)' }}> &middot; {proposals.filter(p => p.status === 'accepted').length} accepted</span>
                )}
              </p>
            </div>
            <span style={{ color: 'var(--color-text-light)', fontSize: 'var(--text-2xl)' }}>&rsaquo;</span>
          </div>
        </Link>
      )}

      {/* Stats Row */}
      <div className="grid grid-cols-4 gap-md mb-lg" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}>
        <StatCard label="Properties" value={properties.length} />
        <StatCard label="Active" value={properties.filter(p => p.pipeline_status === 'active').length} />
        <StatCard label="Commission Earned" value={formatCurrency(totalEarned)} highlight />
        <StatCard label="Pending Payout" value={formatCurrency(totalPending)} />
      </div>

      {/* Properties Table */}
      <div className="card">
        <div className="card-header">
          <h3 className="card-title">Your Properties</h3>
        </div>
        {properties.length === 0 ? (
          <EmptyState
            message="No properties yet"
            hint="Share your referral link with hospitality properties to get started."
          />
        ) : (
          <DataTable
            data={properties.map(p => ({
              id: p.id,
              property_name: p.property_name,
              city: p.city || '-',
              commission_rate: `${p.commission_rate}%`,
              pipeline_status: p.pipeline_status,
            }))}
            columns={[
              { key: 'property_name', label: 'Property' },
              { key: 'city', label: 'City' },
              { key: 'commission_rate', label: 'Rate' },
              { key: 'pipeline_status', label: 'Status', render: (row) => {
                const statusLabels: Record<string, { label: string; bg: string; color: string }> = {
                  new: { label: 'New', bg: 'var(--color-bg)', color: 'var(--color-sidebar-hover)' },
                  in_progress: { label: 'In Progress', bg: 'var(--color-info-bg)', color: 'var(--color-info-text)' },
                  submitted: { label: 'Submitted to HQ', bg: '#f3e8ff', color: 'var(--color-primary-light)' },
                  active: { label: 'Active', bg: 'var(--color-success-bg)', color: 'var(--color-success)' },
                  expired: { label: 'Expired', bg: 'var(--color-warning-bg)', color: 'var(--color-warning)' },
                  lost: { label: 'Lost', bg: 'var(--color-error-bg)', color: 'var(--color-error)' },
                };
                const cfg = statusLabels[row.pipeline_status as string] ?? statusLabels.new;
                return <StatusBadge bg={cfg!.bg} color={cfg!.color}>{cfg!.label}</StatusBadge>;
              }},
            ]}
          />
        )}
      </div>

      {/* Recent Commissions */}
      {commissions.length > 0 && (
        <div className="card mt-lg">
          <div className="card-header">
            <h3 className="card-title">Recent Commissions</h3>
          </div>
          <DataTable
            data={commissions.slice(0, 20).map(c => ({
              id: c.id,
              property: c.hosp_properties?.property_name || 'Unknown',
              base: formatCurrency(parseFloat(c.base_amount)),
              rate: `${c.commission_rate}%`,
              amount: formatCurrency(parseFloat(c.commission_amount)),
              status: c.status,
              date: formatDate(c.created_at)
            }))}
            columns={[
              { key: 'property', label: 'Property' },
              { key: 'base', label: 'Invoice Base' },
              { key: 'rate', label: 'Rate' },
              { key: 'amount', label: 'Commission' },
              { key: 'status', label: 'Status', render: (row) => {
                const cfg = (COMMISSION_STATUS[row.status] ?? COMMISSION_STATUS.pending)!;
                return <StatusBadge bg={cfg.bg} color={cfg.color}>{cfg.label}</StatusBadge>;
              }},
              { key: 'date', label: 'Date' },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default AmbassadorDashboard;
